import React, { useState } from 'react';
import { ClipboardList, InfoCircle, Plus } from '@saleshandy/icons';
import type { IProps } from './types';
import TaskNoteModal from './components/task-note-modal';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import { OverlayTooltip } from '../../../../../../../shared/design-system/components/overlay';
import toaster, { Theme } from '../../../../../../../shared/toaster';
import {
  convertHtmlToMergeTagFormat,
  getTagsOptionsForInlineDropdown,
  stripMergeTagHtml,
} from '../../../../../../../shared/editor/helpers';

const TaskNote: React.FC<IProps> = ({
  taskNote,
  mergeTagOptions,
  onTaskNoteSave,
  onContentBlur,
}) => {
  const [isAddTaskModalVisible, setAddTaskModalVisible] = useState<boolean>(
    false,
  );

  const showModal = () => setAddTaskModalVisible(true);
  const hideModal = () => setAddTaskModalVisible(false);

  const stripHtmlTags = (text: string): string => {
    if (!text) return '';

    // Create a temporary DOM element to decode HTML entities
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;

    // Get text content which automatically decodes HTML entities
    const decodedText = tempDiv.textContent || tempDiv.innerText || '';

    // Remove HTML tags using regex
    return decodedText.replace(/<[^>]*>/g, '');
  };

  const convertMergeTagsToChips = (text: string): string => {
    if (!text || !mergeTagOptions.length) return text;

    // Convert MergeTagOption[] to TagsOptionsForInlineDropdown format
    const tagsOptionsForInlineDropdown = getTagsOptionsForInlineDropdown(
      mergeTagOptions,
      false,
    );

    // Convert merge tags to chips using the helper function
    return convertHtmlToMergeTagFormat(text, tagsOptionsForInlineDropdown);
  };
  const handleSubmit = (note: string) => {
    // Convert chips back to merge tags before saving
    const noteWithMergeTags = stripMergeTagHtml(note);
    // Strip HTML tags and trim the note to remove leading and trailing whitespace
    const cleanNote = stripHtmlTags(noteWithMergeTags)?.trim();
    onTaskNoteSave(cleanNote);
    hideModal();

    toaster.success('Task note has been added successfully.', {
      theme: Theme.New,
    });
  };

  const truncateText = (text: string, maxLength: number): string => {
    // First strip HTML tags, then trim the text to remove leading and trailing whitespace
    const cleanText = stripHtmlTags(text)?.trim();

    if (cleanText.length > maxLength) {
      // Truncate the text and add "..." at the end
      return `${cleanText.substring(0, maxLength)}...`;
    }

    // Text length is within the limit, no need to truncate
    return cleanText;
  };

  return (
    <>
      <div className="d-flex align-items-center bs-mt-20">
        <span className="regular-2 align-items-center font-medium blue-txt-11">
          {stripHtmlTags(taskNote)?.trim()?.length > 0 ? (
            <OverlayTooltip
              text={truncateText(taskNote, 200)}
              className="tooltip-mdd tooltip-white"
            >
              <span
                className="d-flex align-items-center"
                {...accessibleOnClick(showModal)}
              >
                <ClipboardList width={16} height={16} />
                <span className="pointer">Edit Task Instruction</span>
              </span>
            </OverlayTooltip>
          ) : (
            <span className="d-flex align-items-center">
              <span
                className="pointer d-flex align-items-center"
                {...accessibleOnClick(showModal)}
              >
                <Plus width={16} height={16} className="mr-1" />
                <span>Add Task Instruction</span>
              </span>
              <OverlayTooltip text="Add instructions/tips for task assignee to execute this task successfully.">
                <InfoCircle
                  width={16}
                  height={16}
                  className="gray-txt-11 ml-1"
                />
              </OverlayTooltip>
            </span>
          )}
        </span>
      </div>
      {isAddTaskModalVisible && (
        <TaskNoteModal
          show={isAddTaskModalVisible}
          onClose={hideModal}
          taskNote={convertMergeTagsToChips(taskNote)}
          mergeTagOptions={mergeTagOptions}
          contentAreaPlaceholder="Give details to a person who will perform this task, so as not to forget anything."
          onSubmit={(note: string) => handleSubmit(note)}
          onContentBlur={onContentBlur}
        />
      )}
    </>
  );
};

export default TaskNote;
